import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { X, Search, Loader2, ChevronDown } from 'lucide-react'
import { ChatMessageBubble } from './ChatMessageBubble'
import { ChatComposer } from './ChatComposer'
import { ChatDateSeparator } from './ChatDateSeparator'
import { ChatSystemMessage } from './ChatSystemMessage'
import { ChatReplyPreview } from './ChatReplyPreview'
import { ChatMessageActionSheet } from './ChatMessageActionSheet'
import { TextSelectSheet } from './TextSelectSheet'
import { ShareMessageSheet } from './ShareMessageSheet'
import { TaskPickerForChat } from './TaskPickerForChat'
import { MemoLinkModal } from '../tasks/MemoLinkModal'
import type { ChatMessage, ChatMessagePayload, SharedGroupMember, SharedTask } from '@/lib/sharedTypes'

interface ChatPanelProps {
  messages: ChatMessage[]
  members: SharedGroupMember[]
  tasks: SharedTask[]
  currentUid?: string
  lastReadChatAt?: string
  hasMoreMessages?: boolean
  onSend: (payload: ChatMessagePayload) => Promise<void>
  onDelete: (messageId: string) => void
  onReactionAdd: (messageId: string, emoji: string) => void
  onReactionRemove: (messageId: string, emoji: string) => void
  onUploadImage: (file: File) => Promise<{ url: string; width: number; height: number; name: string }>
  onUploadVoice?: (blob: Blob) => Promise<{ url: string; duration: number }>
  onUploadFile?: (file: File) => Promise<{ url: string; name: string; size: number; type: string }>
  onUpdateReadPosition: () => void
  onLoadMore?: () => Promise<void>
  onTyping?: (isTyping: boolean) => void
  groupId?: string
  onToggleTask?: (taskId: string, currentStatus: 'pending' | 'completed') => void
  onAddTask?: (task: { title: string; priority?: string; dueDate?: string }) => Promise<void>
}

export function ChatPanel({
  messages, members, tasks, currentUid, lastReadChatAt,
  hasMoreMessages = true,
  onSend, onDelete, onReactionAdd, onReactionRemove,
  onUploadImage, onUploadVoice, onUploadFile, onUpdateReadPosition,
  onLoadMore, onTyping, groupId, onToggleTask, onAddTask,
}: ChatPanelProps) {
  const [replyingTo, setReplyingTo] = useState<ChatMessage | null>(null)
  const [showTaskPicker, setShowTaskPicker] = useState(false)
  const [showMemoPicker, setShowMemoPicker] = useState(false)
  const [searchMode, setSearchMode] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [actionMessage, setActionMessage] = useState<ChatMessage | null>(null)
  const [selectCopyMessage, setSelectCopyMessage] = useState<ChatMessage | null>(null)
  const [shareMessage, setShareMessage] = useState<ChatMessage | null>(null)
  const [showScrollFAB, setShowScrollFAB] = useState(false)
  const lastScrollFABRef = useRef(false)
  const [newMsgCount, setNewMsgCount] = useState(0)
  const [highlightMsgId, setHighlightMsgId] = useState<string | null>(null)
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const newMessageLineRef = useRef<HTMLDivElement>(null)
  const initialScrollDone = useRef(false)
  const messageAreaRef = useRef<HTMLDivElement>(null)
  const savedLastReadRef = useRef(lastReadChatAt)
  const loadMoreSentinel = useRef<HTMLDivElement>(null)
  const isLoadingMoreRef = useRef(false)

  // 그룹 전환 시 마지막 읽음 위치 갱신 + 스크롤 리셋
  useEffect(() => {
    savedLastReadRef.current = lastReadChatAt
    initialScrollDone.current = false
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId])

  // B1: ref로 최신 콜백 유지 (부모에서 인라인 arrow 전달 → deps에 넣으면 매 렌더 실행)
  const onUpdateReadPositionRef = useRef(onUpdateReadPosition)
  onUpdateReadPositionRef.current = onUpdateReadPosition

  // 메시지 정렬 (오래된 것 먼저)
  const sortedMessages = useMemo(
    () => [...messages].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    ),
    [messages]
  )

  // 읽음 위치 업데이트 — 새 메시지 시 2초 디바운스 (진입 즉시 호출 제거: 플래싱 방지)
  useEffect(() => {
    const timer = setTimeout(() => {
      onUpdateReadPositionRef.current()
    }, 2000)
    return () => clearTimeout(timer)
  }, [messages.length])

  // 자동 스크롤: 초기 진입 시 "여기부터 새 메시지" 위치로, 이후 새 메시지는 맨 아래로
  useEffect(() => {
    if (!initialScrollDone.current) {
      // 최초 진입: 안읽은 메시지 분리선이 있으면 그 위치로, 없으면 맨 아래
      initialScrollDone.current = true
      // 이중 rAF: 첫 프레임에서 DOM 레이아웃, 두 번째 프레임에서 스크롤 (이미지 등 비동기 렌더 대기)
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (newMessageLineRef.current) {
            newMessageLineRef.current.scrollIntoView({ block: 'start' })
          } else {
            messagesEndRef.current?.scrollIntoView()
          }
        })
      })
    } else {
      // 이후 새 메시지 도착: 스크롤이 하단 근처면 자동 스크롤, 아니면 유지
      const area = messageAreaRef.current
      if (area) {
        const isNearBottom = area.scrollHeight - area.scrollTop - area.clientHeight < 150
        if (isNearBottom) {
          messagesEndRef.current?.scrollIntoView({
            behavior: 'scrollBehavior' in document.documentElement.style ? 'smooth' : 'auto',
          })
        } else {
          // 스크롤이 위에 있으면 새 메시지 카운트 증가
          setNewMsgCount((prev) => prev + 1)
        }
      }
    }
  }, [sortedMessages.length])

  // 읽지 않은 수 계산 — 본인은 항상 읽음 처리 (lastReadChatAt 디바운스 지연 무관)
  const getUnreadCount = useCallback((msgCreatedAt: string) => {
    const active = members.filter((m) => m.status === 'active')
    const readCount = active.filter((m) =>
      m.uid === currentUid || (m.lastReadChatAt && m.lastReadChatAt >= msgCreatedAt)
    ).length
    const unread = active.length - readCount
    return unread > 0 ? unread : 0
  }, [members, currentUid])

  // 날짜 구분 체크
  const getDateString = (iso: string) => iso.split('T')[0]

  // 페이지네이션: 스크롤 상단 감지 — isLoadingMore를 ref로 관리 (observer 재생성 방지)
  useEffect(() => {
    if (!onLoadMore || !hasMoreMessages || !loadMoreSentinel.current) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isLoadingMoreRef.current) {
          isLoadingMoreRef.current = true
          setIsLoadingMore(true)
          onLoadMore().finally(() => { isLoadingMoreRef.current = false; setIsLoadingMore(false) })
        }
      },
      { threshold: 0.1 }
    )
    observer.observe(loadMoreSentinel.current)
    return () => observer.disconnect()
  }, [onLoadMore, hasMoreMessages])

  const handleTaskSelect = async (task: SharedTask) => {
    setShowTaskPicker(false)
    await onSend({
      text: `${task.title}`,
      type: 'task_card',
      taskRef: task.id,
      taskSnapshot: {
        title: task.title,
        status: task.status,
        priority: task.priority,
        assigneeName: task.assigneeNames?.join(', ') || task.assigneeName,
        dueDate: task.dueDate,
      },
    })
  }

  // 검색 필터링
  const displayMessages = useMemo(() => {
    if (!searchMode || !searchQuery.trim()) return sortedMessages
    const q = searchQuery.toLowerCase()
    return sortedMessages.filter((m) => m.text?.toLowerCase().includes(q) || m.userName?.toLowerCase().includes(q))
  }, [sortedMessages, searchMode, searchQuery])

  // 메시지 id → 메시지 조회용 ref (stable 콜백이 항상 최신 메시지를 찾도록)
  const messagesByIdRef = useRef(new Map<string, ChatMessage>())
  messagesByIdRef.current = useMemo(() => {
    const map = new Map<string, ChatMessage>()
    for (const m of displayMessages) map.set(m.id, m)
    return map
  }, [displayMessages])

  // 메시지별 안읽은 수를 렌더당 1회 precompute (버블마다 O(members) 재계산 방지)
  const unreadCountByCreatedAt = useMemo(() => {
    const map = new Map<string, number>()
    for (const m of displayMessages) {
      if (!map.has(m.createdAt)) map.set(m.createdAt, getUnreadCount(m.createdAt))
    }
    return map
  }, [displayMessages, getUnreadCount])

  // 버블에 전달할 stable 콜백들 — 메시지 id를 받아 ref에서 최신 메시지를 조회한다.
  // (React.memo가 효과를 내려면 props 콜백이 매 렌더마다 새 함수면 안 됨)
  const handleReply = useCallback((msgId: string) => {
    const msg = messagesByIdRef.current.get(msgId)
    if (msg) setReplyingTo(msg)
  }, [])

  const handleLongPress = useCallback((msgId: string) => {
    const msg = messagesByIdRef.current.get(msgId)
    if (msg) setActionMessage(msg)
  }, [])

  const handleScrollToMessage = useCallback((msgId: string) => {
    const el = document.querySelector(`[data-message-id="${msgId}"]`)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      setHighlightMsgId(msgId)
      setTimeout(() => setHighlightMsgId(null), 2000)
    }
  }, [])

  // 부모 콜백을 ref로 래핑해 stable한 핸들러로 노출 (부모가 인라인 arrow 전달)
  const onDeleteRef = useRef(onDelete)
  onDeleteRef.current = onDelete
  const onReactionAddRef = useRef(onReactionAdd)
  onReactionAddRef.current = onReactionAdd
  const onReactionRemoveRef = useRef(onReactionRemove)
  onReactionRemoveRef.current = onReactionRemove
  const onToggleTaskRef = useRef(onToggleTask)
  onToggleTaskRef.current = onToggleTask

  const handleDelete = useCallback((msgId: string) => { onDeleteRef.current(msgId) }, [])
  const handleReactionAdd = useCallback((msgId: string, emoji: string) => { onReactionAddRef.current(msgId, emoji) }, [])
  const handleReactionRemove = useCallback((msgId: string, emoji: string) => { onReactionRemoveRef.current(msgId, emoji) }, [])
  const handleToggleTask = useCallback((taskId: string, status: 'pending' | 'completed') => {
    onToggleTaskRef.current?.(taskId, status)
  }, [])

  // 타이핑 중인 멤버
  const typingMembers = useMemo(() => {
    const now = Date.now()
    return members.filter((m) => {
      if (m.uid === currentUid || !m.isTyping) return false
      // lastActiveAt이 10초 이상 지난 멤버는 타이핑 상태가 아닌 것으로 간주 (stale 방지)
      if (m.lastActiveAt) {
        const elapsed = now - new Date(m.lastActiveAt).getTime()
        if (elapsed > 10_000) return false
      }
      return true
    })
  }, [members, currentUid])

  // 새 메시지 분리선 위치 (진입 시 캡처된 lastReadChatAt 기준)
  const newMessageIdx = useMemo(() => {
    const readAt = savedLastReadRef.current
    if (!readAt) return -1
    return displayMessages.findIndex((m) => m.createdAt > readAt && m.userId !== currentUid)
  }, [displayMessages, currentUid])

  return (
    <div className="flex flex-col h-full">
      {/* 검색 바 */}
      {searchMode && (
        <div className="flex items-center gap-2 px-3 py-2 border-b border-base bg-surface-secondary">
          <Search className="w-4 h-4 text-caption flex-shrink-0" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="메시지 검색..."
            className="flex-1 bg-transparent text-sm text-heading outline-none placeholder:text-disabled"
            autoFocus
          />
          <button onClick={() => { setSearchMode(false); setSearchQuery('') }} className="p-1 text-caption hover:text-heading">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* 검색 토글 버튼 (검색 모드가 아닐 때) */}
      {!searchMode && (
        <div className="flex justify-end px-2 py-1">
          <button
            onClick={() => setSearchMode(true)}
            className="p-1.5 text-caption hover:text-heading hover-surface rounded-lg transition-colors"
            title="메시지 검색"
          >
            <Search className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* 메시지 영역 */}
      <div
        ref={messageAreaRef}
        className="flex-1 overflow-y-auto px-1 space-y-0.5 bg-inherit relative"
        onScroll={() => {
          const area = messageAreaRef.current
          if (!area) return
          const dist = area.scrollHeight - area.scrollTop - area.clientHeight
          const shouldShow = dist > 300
          if (shouldShow !== lastScrollFABRef.current) {
            lastScrollFABRef.current = shouldShow
            setShowScrollFAB(shouldShow)
          }
          if (dist < 150) setNewMsgCount(0)
        }}
      >
        {/* 페이지네이션 sentinel */}
        {hasMoreMessages && onLoadMore && (
          <div ref={loadMoreSentinel} className="flex justify-center py-2">
            {isLoadingMore && <Loader2 className="w-4 h-4 animate-spin text-caption" />}
          </div>
        )}

        {displayMessages.length === 0 && (
          <div className="text-center py-12 text-caption text-sm">
            {searchMode ? '검색 결과가 없습니다' : '아직 메시지가 없습니다'}
          </div>
        )}
        {displayMessages.map((msg, idx) => {
          const prevMsg = idx > 0 ? displayMessages[idx - 1] : null
          const nextMsg = idx < displayMessages.length - 1 ? displayMessages[idx + 1] : null
          const showDate = !prevMsg || getDateString(prevMsg.createdAt) !== getDateString(msg.createdAt)
          const msgType = msg.type || 'text'

          // 연속 메시지 그룹핑: 같은 유저 + 1분 이내 + 시스템 아님
          const isGrouped = !!prevMsg
            && prevMsg.userId === msg.userId
            && Math.abs(new Date(msg.createdAt).getTime() - new Date(prevMsg.createdAt).getTime()) < 60000
            && prevMsg.type !== 'system' && msgType !== 'system'
            && !showDate

          // 시간 표시: 그룹 마지막 메시지에만
          const isLastInGroup = !nextMsg
            || nextMsg.userId !== msg.userId
            || Math.abs(new Date(nextMsg.createdAt).getTime() - new Date(msg.createdAt).getTime()) >= 60000
            || nextMsg.type === 'system'
            || getDateString(nextMsg.createdAt) !== getDateString(msg.createdAt)

          return (
            <div key={msg.id} className={highlightMsgId === msg.id ? 'bg-primary-100/50 dark:bg-primary-900/30 rounded-xl transition-colors duration-500' : ''}>
              {showDate && <ChatDateSeparator date={getDateString(msg.createdAt)} />}
              {/* 새 메시지 분리선 */}
              {idx === newMessageIdx && (
                <div ref={newMessageLineRef} className="flex items-center gap-2 my-2 px-4">
                  <div className="flex-1 h-px bg-primary-300 dark:bg-primary-700" />
                  <span className="text-meta text-primary-500 font-medium whitespace-nowrap">여기부터 새 메시지</span>
                  <div className="flex-1 h-px bg-primary-300 dark:bg-primary-700" />
                </div>
              )}
              {msgType === 'system' ? (
                <ChatSystemMessage message={msg} />
              ) : (
                <ChatMessageBubble
                  message={msg}
                  isOwn={msg.userId === currentUid}
                  isGrouped={isGrouped}
                  showTime={isLastInGroup}
                  groupId={groupId}
                  onReply={handleReply}
                  onReactionAdd={handleReactionAdd}
                  onReactionRemove={handleReactionRemove}
                  onDelete={handleDelete}
                  onLongPress={handleLongPress}
                  onToggleTask={handleToggleTask}
                  onScrollToMessage={handleScrollToMessage}
                  unreadCount={unreadCountByCreatedAt.get(msg.createdAt) ?? 0}
                  members={members}
                />
              )}
            </div>
          )
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* 하단 이동 FAB */}
      {showScrollFAB && (
        <div className="absolute right-4 z-10" style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 70px)' }}>
          <button
            onClick={() => {
              messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
              setNewMsgCount(0)
            }}
            className="w-10 h-10 rounded-full bg-surface-primary border border-base shadow-lg flex items-center justify-center hover:bg-surface-secondary transition-colors relative"
          >
            <ChevronDown className="w-5 h-5 text-heading" />
            {newMsgCount > 0 && (
              <span className="absolute -top-2 -right-1 min-w-[20px] h-5 px-1.5 flex items-center justify-center rounded-full text-meta font-bold text-white bg-red-500">
                {newMsgCount > 99 ? '99+' : newMsgCount}
              </span>
            )}
          </button>
        </div>
      )}

      {/* 타이핑 인디케이터 */}
      {typingMembers.length > 0 && (
        <div className="px-3 py-1 text-xs text-caption animate-pulse">
          {typingMembers.map((m) => m.displayName).join(', ')}님이 입력 중...
        </div>
      )}

      {/* 답장 프리뷰 */}
      {replyingTo && (
        <div className="px-3 pt-2">
          <ChatReplyPreview
            userName={replyingTo.userName}
            text={replyingTo.text}
            imageUrl={replyingTo.imageUrl}
            onCancel={() => setReplyingTo(null)}
          />
        </div>
      )}

      {/* 입력 영역 (별도 컴포넌트 — 키 입력이 메시지 목록을 리렌더하지 않도록 분리) */}
      <ChatComposer
        messages={messages}
        members={members}
        tasks={tasks}
        lastReadChatAt={lastReadChatAt}
        replyingTo={replyingTo}
        onSend={onSend}
        onUploadImage={onUploadImage}
        onUploadVoice={onUploadVoice}
        onUploadFile={onUploadFile}
        onTyping={onTyping}
        onAddTask={onAddTask}
        onClearReply={() => setReplyingTo(null)}
        onOpenTaskPicker={() => setShowTaskPicker(true)}
        onOpenMemoPicker={() => setShowMemoPicker(true)}
      />

      {/* 할일 선택 모달 */}
      <TaskPickerForChat
        isOpen={showTaskPicker}
        onClose={() => setShowTaskPicker(false)}
        tasks={tasks}
        onSelect={handleTaskSelect}
      />

      {/* 메모 공유 모달 */}
      <MemoLinkModal
        isOpen={showMemoPicker}
        onClose={() => setShowMemoPicker(false)}
        onSelect={async (memo) => {
          try {
            await onSend({
              text: memo.memoTitle,
              type: 'memo_card',
              memoSnapshot: {
                syncId: memo.memoSyncId,
                title: memo.memoTitle,
                preview: memo.memoPreview || '',
                tags: memo.tags || [],
                color: memo.memoColor,
              },
            })
          } catch {
            import('@/stores/toastStore').then(({ useToastStore }) => {
              useToastStore.getState().showToast('메모 공유에 실패했습니다.', 'error')
            })
          }
        }}
      />

      {/* 롱프레스 바텀시트 액션 메뉴 */}
      <ChatMessageActionSheet
        message={actionMessage}
        isOwn={actionMessage?.userId === currentUid}
        onClose={() => setActionMessage(null)}
        onReply={() => { if (actionMessage) setReplyingTo(actionMessage) }}
        onCopy={() => {
          if (actionMessage?.text) {
            navigator.clipboard.writeText(actionMessage.text).catch(() => {})
          }
        }}
        onSelectCopy={() => { if (actionMessage) setSelectCopyMessage(actionMessage) }}
        onShare={() => { if (actionMessage) setShareMessage(actionMessage) }}
        onAnnounce={async () => {
          if (actionMessage?.text && groupId) {
            const { useSharedGroupStore } = await import('@/stores/sharedGroupStore')
            const { useAuthStore } = await import('@/stores/authStore')
            const user = useAuthStore.getState().user
            await useSharedGroupStore.getState().updateGroup(groupId, {
              announcement: actionMessage.text,
              announcementSetBy: user?.uid || '',
              announcementSetByName: user?.displayName || user?.email || '',
              announcementSetAt: new Date().toISOString(),
            })
            const { useToastStore } = await import('@/stores/toastStore')
            useToastStore.getState().showToast('공지로 설정되었습니다.', 'success')
          }
        }}
        onCapture={async () => {
          if (!actionMessage) return
          const msgEl = document.querySelector(`[data-message-id="${actionMessage.id}"]`)
          if (!msgEl) return
          try {
            const { default: html2canvas } = await import('html2canvas')
            const canvas = await html2canvas(msgEl as HTMLElement, { backgroundColor: null, scale: 2 })
            canvas.toBlob((blob) => {
              if (!blob) return
              const url = URL.createObjectURL(blob)
              const a = document.createElement('a')
              a.href = url
              a.download = `chat_${actionMessage.id.slice(0, 8)}.png`
              a.click()
              URL.revokeObjectURL(url)
            })
          } catch {
            const { useToastStore } = await import('@/stores/toastStore')
            useToastStore.getState().showToast('캡처에 실패했습니다.', 'error')
          }
        }}
        onDelete={() => { if (actionMessage) onDelete(actionMessage.id) }}
        onReaction={(emoji) => { if (actionMessage) onReactionAdd(actionMessage.id, emoji) }}
      />

      {/* 선택 복사 시트 */}
      <TextSelectSheet
        text={selectCopyMessage?.text || ''}
        userName={selectCopyMessage?.userName || ''}
        isOpen={!!selectCopyMessage}
        onClose={() => setSelectCopyMessage(null)}
      />

      {/* 메시지 공유 시트 */}
      <ShareMessageSheet
        message={shareMessage}
        currentGroupId={groupId || ''}
        isOpen={!!shareMessage}
        onClose={() => setShareMessage(null)}
      />
    </div>
  )
}
